import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SwapOutlined } from '@ant-design/icons';
import { Button, Slider, Tooltip } from 'antd';
import type {
  AssessmentAssignment,
  AssessmentInstrument,
  LatestAFSDV01,
  Session,
} from 'state';

import { formatters } from 'services';
import { recordDragDiagnostic } from 'services/dragDiagnostics';
import { isSimplifiedOptionSettlementFailed } from '../../../state/app/simplified_options/presentation';
import type { ExecutionIntent } from '../MarketsTabV3/executionModel';
import { executionTargetsInstrument } from '../MarketsTabV3/executionModel';
import {
  ordinaryQuoteIsAvailable,
  sessionBlocksTrading,
} from '../MarketsTabV3/model';
import type { MarketInstrument, SelectInstrument } from '../MarketsTabV3/types';
import InstrumentActions from './InstrumentActions';
import MarketMovement from './MarketMovement';
import {
  getAssessmentStorage,
  latestAssignment,
  laterAssignmentLedgerKey,
  type LaterAssignmentLedgerEntry,
  matchingLaterAssignment,
  readLaterAssignmentLedger,
  readV4NewsDragData,
  writeLaterAssignmentLedger,
} from './assessmentUi';
import { optionSecondaryMetric } from './optionMetrics';

export type InstrumentGroup = {
  key: 'fx' | 'etf' | 'equity' | 'option' | 'bond';
  label: string;
  rows: MarketInstrument[];
};

type Props = {
  groups: InstrumentGroup[];
  session: Session;
  reportingCurrencySymbol: string;
  selectedInstrumentId?: number;
  selectInstrument: SelectInstrument;
  openInstrumentOrder: SelectInstrument;
  executionIntent: ExecutionIntent;
  openODA: (instrumentId: number) => void;
  showETFDetails: (assetId: number) => void;
  assessment?: V4AssessmentControls;
  fitMarketValues?: boolean;
  showBondCountryMaturityLabel?: boolean;
  bondDv01Snapshot?: LatestAFSDV01 | null;
  hideInstrumentTicker?: boolean;
};

export type V4AssessmentControls = {
  attached: boolean;
  canWrite: boolean;
  persistWrites: boolean;
  currentPeriod?: number | null;
  scenario?: string;
  playerId: number;
  instruments: AssessmentInstrument[];
  assignments: AssessmentAssignment[];
  sliders: Record<string, number>;
  headlines: Record<number, string>;
  currentNewsIds: ReadonlySet<number>;
  assignNews: (payload: {
    news: number;
    instrument_code: string;
    temp_id: number;
  }) => void;
  revokeAssignment: (assignmentId: number) => void;
  setSlider: (
    instrumentCode: string,
    value: number,
    previousValue?: number,
  ) => void;
};

const quote = (
  row: MarketInstrument,
  side: 'bid' | 'ask',
  fitValue: boolean,
) => (
  <MarketMovement
    value={formatters.priceFormat(
      row.asset[side],
      row.asset.ticker,
      row.currencySymbol,
      row.asset.resourcetype,
    )}
    priceDiff={row.asset[`period_${side}_diff`] ?? row.asset[`${side}_diff`]}
    fitValue={fitValue}
  />
);

const bondYield = (row: MarketInstrument, quoted: boolean) => {
  const value = row.asset.ask_yield;
  return quoted && value != null && value !== 0 && Number.isFinite(value)
    ? `${formatters.commasExact3Digits(value)}%`
    : '—';
};

export const instrumentDisplayName = (
  row: MarketInstrument,
  showBondCountryMaturityLabel = false,
) => {
  const fallback = row.asset.name || row.asset.ticker;
  if (!showBondCountryMaturityLabel || row.asset.resourcetype !== 'AFSBond') {
    return fallback;
  }

  const countryCode = row.asset.country_code?.trim();
  const initialTenor = row.asset.initial_tenor_years;
  const countryCodeIsMissing =
    !countryCode || ['n/a', 'none', 'null'].includes(countryCode.toLowerCase());
  if (countryCodeIsMissing || initialTenor == null) return fallback;

  return `${countryCode}${formatters.commas1Digit(initialTenor)}Y`;
};

const pnlClass = (value: number) =>
  value > 0 ? 'is-positive' : value < 0 ? 'is-negative' : '';

const formatDv01 = (value?: number | null) =>
  value == null || !Number.isFinite(value)
    ? '—'
    : formatters.commas2Digits(Number(value.toFixed(2)));

const formatDv01SnapshotDate = (scenarioDate?: string) => {
  if (!scenarioDate) return null;
  const date = new Date(scenarioDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const FALLBACK_GROUP_HEADER_HEIGHT = 24;

let assessmentTempId = -1;
const nextAssessmentTempId = () => assessmentTempId--;
const PREVIEW_ASSIGNMENT_ID_START = 2_000_000_000;
let previewAssignmentId = PREVIEW_ASSIGNMENT_ID_START;
const nextPreviewAssignmentId = () => previewAssignmentId++;

type DisplayAssignment = {
  assignment: AssessmentAssignment;
  timing: 'current' | 'past';
};

const assignmentHeadline = (
  assignment: AssessmentAssignment,
  headlines: Record<number, string>,
) => headlines[assignment.news] || `News #${assignment.news}`;

const sameKeys = (
  left: InstrumentGroup['key'][],
  right: InstrumentGroup['key'][],
) =>
  left.length === right.length &&
  left.every((key, index) => key === right[index]);

const InstrumentStack: React.FC<Props> = ({
  groups,
  session,
  reportingCurrencySymbol,
  selectedInstrumentId,
  selectInstrument,
  openInstrumentOrder,
  executionIntent,
  openODA,
  showETFDetails,
  assessment,
  fitMarketValues = false,
  showBondCountryMaturityLabel = false,
  bondDv01Snapshot,
  hideInstrumentTicker = false,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef(new Map<string, HTMLElement>());
  const dragDepthRef = useRef(new Map<string, number>());
  const [bottomGroupKeys, setBottomGroupKeys] = useState<
    InstrumentGroup['key'][]
  >([]);
  const [dragTargetCode, setDragTargetCode] = useState<string>();
  const clearDrag = useCallback(() => {
    dragDepthRef.current.clear();
    setDragTargetCode(undefined);
  }, []);
  useEffect(() => {
    clearDrag();
    const clearWhenHidden = () => {
      if (document.hidden) clearDrag();
    };
    const events = ['dragstart', 'dragend', 'drop', 'pointerdown'] as const;
    events.forEach(name => document.addEventListener(name, clearDrag, true));
    document.addEventListener('visibilitychange', clearWhenHidden);
    return () => {
      events.forEach(name =>
        document.removeEventListener(name, clearDrag, true),
      );
      document.removeEventListener('visibilitychange', clearWhenHidden);
    };
  }, [
    clearDrag,
    assessment?.canWrite,
    session.id,
    session.status,
    session.elapsed_days,
  ]);
  const [expandedPastCodes, setExpandedPastCodes] = useState<Set<string>>(
    new Set(),
  );
  const [draggingSliders, setDraggingSliders] = useState<
    Record<string, number>
  >({});
  const [previewSliders, setPreviewSliders] = useState<Record<string, number>>(
    {},
  );
  const [previewAssignments, setPreviewAssignments] = useState<
    AssessmentAssignment[]
  >([]);

  const ledgerKey = useMemo(() => {
    if (
      !assessment?.attached ||
      assessment.currentPeriod === null ||
      assessment.currentPeriod === undefined
    ) {
      return undefined;
    }
    return laterAssignmentLedgerKey({
      sessionId: session.id,
      playerId: assessment.playerId,
      scenario: assessment.scenario,
      currentPeriod: assessment.currentPeriod,
    });
  }, [
    assessment?.attached,
    assessment?.currentPeriod,
    assessment?.playerId,
    assessment?.scenario,
    session.id,
  ]);
  const [laterLedger, setLaterLedger] = useState<LaterAssignmentLedgerEntry[]>(
    [],
  );

  useEffect(() => {
    setLaterLedger(
      readLaterAssignmentLedger(getAssessmentStorage(), ledgerKey),
    );
    setExpandedPastCodes(new Set());
    setDraggingSliders({});
    setPreviewSliders({});
    setPreviewAssignments([]);
    setDragTargetCode(undefined);
    dragDepthRef.current.clear();
  }, [ledgerKey]);

  /**
   * Capture participation wherever the API permits it, independently of the
   * scoring policy. Informational views can be saved without affecting scores.
   *
   * Join on the per-session resolved id list: one assessment code can span
   * several session assets (sequential bond issues), and the legacy single
   * `underlying_asset` FK goes stale across blueprint regenerations (prod:
   * US2Y pointed at an asset its session never held, so no 2Y row got
   * conviction/news controls). The single FK is only the fallback for an API
   * that predates `underlying_assets`.
   */
  const { assessmentInstrumentByAsset, nonRecordingInstrumentByAsset } =
    useMemo(() => {
      const permitted = new Map<number, AssessmentInstrument>();
      const remaining = new Map<number, AssessmentInstrument>();
      (assessment?.instruments || []).forEach(instrument => {
        const directConvictionAllowed =
          instrument.conviction_allowed !== undefined
            ? instrument.conviction_allowed
            : instrument.kind !== 'option';
        const assetIds = instrument.underlying_assets?.length
          ? instrument.underlying_assets
          : instrument.underlying_asset != null
            ? [instrument.underlying_asset]
            : [];
        const target = directConvictionAllowed ? permitted : remaining;
        assetIds.forEach(assetId => target.set(assetId, instrument));
      });
      return {
        assessmentInstrumentByAsset: permitted,
        nonRecordingInstrumentByAsset: remaining,
      };
    }, [assessment?.instruments]);

  const assignmentsByCode = useMemo(() => {
    const byCode = new Map<string, AssessmentAssignment[]>();
    [...(assessment?.assignments || []), ...previewAssignments].forEach(
      assignment => {
        const rows = byCode.get(assignment.instrument_code) || [];
        rows.push(assignment);
        byCode.set(assignment.instrument_code, rows);
      },
    );
    return byCode;
  }, [assessment?.assignments, previewAssignments]);

  const updateLaterLedger = useCallback(
    (entries: LaterAssignmentLedgerEntry[]) => {
      setLaterLedger(entries);
      writeLaterAssignmentLedger(getAssessmentStorage(), ledgerKey, entries);
    },
    [ledgerKey],
  );

  const headerHeight = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return FALLBACK_GROUP_HEADER_HEIGHT;
    const configuredHeight = Number.parseFloat(
      window
        .getComputedStyle(scroller)
        .getPropertyValue('--mv4-group-header-height'),
    );
    return configuredHeight || FALLBACK_GROUP_HEADER_HEIGHT;
  };

  const syncBottomLabels = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || scroller.clientHeight <= 0) return;

    const height = headerHeight();
    const scrollTop = scroller.scrollTop;
    const reachedTopCount = groups.reduce((count, group, groupIndex) => {
      const section = sectionRefs.current.get(group.key);
      if (!section) return count;
      const topTarget = Math.max(
        0,
        section.offsetTop - (groupIndex + 1) * height,
      );
      return scrollTop >= topTarget - 1 ? groupIndex + 1 : count;
    }, 0);
    const nextKeys = groups
      .filter((group, groupIndex) => {
        const section = sectionRefs.current.get(group.key);
        if (!section) return false;
        const headerFlowTop = section.offsetTop - height - scrollTop;
        const bottomSlotTop =
          scroller.clientHeight - (groups.length - groupIndex) * height;
        const clearsTopStack = bottomSlotTop >= reachedTopCount * height;
        return clearsTopStack && headerFlowTop > bottomSlotTop + 1;
      })
      .map(group => group.key);

    setBottomGroupKeys(current =>
      sameKeys(current, nextKeys) ? current : nextKeys,
    );
  }, [groups]);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    syncBottomLabels();
    scroller.addEventListener('scroll', syncBottomLabels, { passive: true });
    const resizeObserver = new ResizeObserver(syncBottomLabels);
    resizeObserver.observe(scroller);
    sectionRefs.current.forEach(section => resizeObserver.observe(section));

    return () => {
      scroller.removeEventListener('scroll', syncBottomLabels);
      resizeObserver.disconnect();
    };
  }, [syncBottomLabels]);

  const scrollToGroup = (groupKey: string, groupIndex: number) => {
    const scroller = scrollerRef.current;
    const section = sectionRefs.current.get(groupKey);
    if (!scroller || !section) return;

    const stickyHeaderHeight = headerHeight();
    const top = Math.max(
      0,
      section.offsetTop - (groupIndex + 1) * stickyHeaderHeight,
    );
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    scroller.scrollTo({
      top,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  const assignmentsForInstrument = (instrumentCode: string) =>
    assignmentsByCode.get(instrumentCode) || [];

  const currentAssignmentsFor = (
    instrumentCode: string,
  ): DisplayAssignment[] => {
    if (!assessment) return [];
    const byNews = new Map<number, AssessmentAssignment[]>();
    assignmentsForInstrument(instrumentCode).forEach(assignment => {
      if (!assessment.currentNewsIds.has(assignment.news)) return;
      const rows = byNews.get(assignment.news) || [];
      rows.push(assignment);
      byNews.set(assignment.news, rows);
    });
    return Array.from(byNews.values())
      .map(latestAssignment)
      .filter((item): item is AssessmentAssignment => Boolean(item))
      .map(assignment => ({ assignment, timing: 'current' }));
  };

  const laterAssignmentsFor = (instrumentCode: string): DisplayAssignment[] =>
    laterLedger
      .filter(
        entry =>
          entry.instrumentCode === instrumentCode &&
          !assessment?.currentNewsIds.has(entry.newsId),
      )
      .map(entry =>
        matchingLaterAssignment(
          entry,
          assignmentsForInstrument(instrumentCode),
        ),
      )
      .filter((item): item is AssessmentAssignment => Boolean(item))
      .map(assignment => ({ assignment, timing: 'past' }));

  const dropNews = (
    event: React.DragEvent,
    instrument: AssessmentInstrument,
  ) => {
    recordDragDiagnostic(
      'target.drop',
      {
        instrument_code: instrument.code,
        attached: assessment?.attached,
        can_write: assessment?.canWrite,
      },
      true,
    );
    event.preventDefault();
    dragDepthRef.current.delete(instrument.code);
    setDragTargetCode(undefined);
    if (!assessment?.attached || !assessment.canWrite) return;

    const dragged = readV4NewsDragData(event.dataTransfer);
    if (!dragged) {
      recordDragDiagnostic(
        'target.rejected',
        {
          reason: 'invalid_payload',
          instrument_code: instrument.code,
        },
        true,
      );
      return;
    }
    // The current batch in Redux is authoritative if the period changed during
    // a drag; the payload timing is only a source-side hint.
    const isCurrent = assessment.currentNewsIds.has(dragged.newsId);
    const instrumentAssignments = assignmentsForInstrument(instrument.code);

    if (isCurrent) {
      if (
        instrumentAssignments.some(
          assignment => assignment.news === dragged.newsId,
        )
      ) {
        recordDragDiagnostic(
          'target.rejected',
          {
            reason: 'duplicate_current',
            news: dragged.newsId,
            instrument_code: instrument.code,
          },
          true,
        );
        return;
      }
    } else {
      const existingEntry = laterLedger.find(
        entry =>
          entry.newsId === dragged.newsId &&
          entry.instrumentCode === instrument.code,
      );
      if (
        existingEntry &&
        matchingLaterAssignment(existingEntry, instrumentAssignments)
      ) {
        recordDragDiagnostic(
          'target.rejected',
          {
            reason: 'duplicate_past',
            news: dragged.newsId,
            instrument_code: instrument.code,
          },
          true,
        );
        return;
      }
      if (!existingEntry) {
        const afterAssignmentId = Math.max(
          0,
          ...instrumentAssignments
            .filter(assignment => assignment.news === dragged.newsId)
            .map(assignment => assignment.id)
            .filter(id => id > 0),
        );
        updateLaterLedger([
          ...laterLedger,
          {
            newsId: dragged.newsId,
            instrumentCode: instrument.code,
            afterAssignmentId,
          },
        ]);
      }
    }

    recordDragDiagnostic(
      'target.accepted',
      {
        news: dragged.newsId,
        instrument_code: instrument.code,
        current: isCurrent,
        persist_writes: assessment.persistWrites,
      },
      true,
    );
    if (assessment.persistWrites) {
      assessment.assignNews({
        news: dragged.newsId,
        instrument_code: instrument.code,
        temp_id: nextAssessmentTempId(),
      });
    } else {
      setPreviewAssignments(current => [
        ...current,
        {
          id: nextPreviewAssignmentId(),
          news: dragged.newsId,
          instrument_code: instrument.code,
          period: assessment.currentPeriod || 0,
          created_at: new Date().toISOString(),
          revoked_at: null,
        },
      ]);
    }
  };

  const renderAssignment = ({ assignment, timing }: DisplayAssignment) => {
    if (!assessment) return null;
    const headline = assignmentHeadline(assignment, assessment.headlines);
    return (
      <div
        key={`${timing}-${assignment.id}`}
        className="markets-v4-instrument-assignment"
        title={headline}
      >
        <span>{timing === 'current' ? 'This period' : 'Past news'}</span>
        <strong>{headline}</strong>
        {assessment.canWrite &&
        !assignment.pending &&
        (assessment.persistWrites ||
          assignment.id >= PREVIEW_ASSIGNMENT_ID_START) ? (
          <button
            type="button"
            aria-label={`Remove news assignment: ${headline}`}
            onClick={event => {
              event.stopPropagation();
              if (assessment.persistWrites) {
                assessment.revokeAssignment(assignment.id);
              } else {
                setPreviewAssignments(current =>
                  current.filter(item => item.id !== assignment.id),
                );
              }
            }}
          >
            ×
          </button>
        ) : (
          <i
            aria-label={assignment.pending ? 'Saving assignment' : undefined}
          />
        )}
      </div>
    );
  };

  return (
    <div className="markets-v4-instrument-scrollframe">
      <div ref={scrollerRef} className="markets-v4-instrument-groups">
        {groups.map((group, groupIndex) => {
          const titleId = `markets-v4-${group.key}-title`;
          const sectionId = `markets-v4-${group.key}-instruments`;
          const showBondDv01 =
            group.key === 'bond' && bondDv01Snapshot !== undefined;
          const netDv01 = formatDv01(bondDv01Snapshot?.net_dv01);
          const grossDv01 = formatDv01(bondDv01Snapshot?.gross_dv01);
          const netDv01Limit = formatDv01(session.net_dv01_limit);
          const grossDv01Limit = formatDv01(session.gross_dv01_limit);
          const snapshotDate = formatDv01SnapshotDate(
            bondDv01Snapshot?.scenario_date,
          );
          const snapshotStatus = snapshotDate
            ? `Snapshot as of ${snapshotDate}`
            : 'No historical AFS DV01 snapshot';
          const headerAriaLabel = showBondDv01
            ? `${group.label}, ${group.rows.length} instruments. Net DV01 ${netDv01}. Net DV01 limit ${netDv01Limit}. Gross DV01 ${grossDv01}. Gross DV01 limit ${grossDv01Limit}. ${snapshotStatus}.`
            : `${group.label}, ${group.rows.length} instruments`;
          const stickyStyle = {
            '--mv4-sticky-index': groupIndex,
          } as React.CSSProperties;

          return (
            <React.Fragment key={group.key}>
              <header
                className={`markets-v4-instrument-group__header is-${group.key}`}
                style={stickyStyle}
              >
                <h2 id={titleId}>
                  <button
                    type="button"
                    aria-controls={sectionId}
                    aria-label={headerAriaLabel}
                    onClick={() => scrollToGroup(group.key, groupIndex)}
                  >
                    <span className="markets-v4-instrument-group__label">
                      {group.label}
                    </span>
                    <span
                      className="markets-v4-instrument-group__count"
                      aria-hidden="true"
                    >
                      {group.rows.length}
                    </span>
                    {showBondDv01 && (
                      <span
                        className="markets-v4-instrument-group__dv01"
                        title={snapshotStatus}
                        aria-hidden="true"
                      >
                        <span className="markets-v4-instrument-group__dv01-metric">
                          <span>Net DV01</span>
                          <strong
                            className={pnlClass(
                              bondDv01Snapshot?.net_dv01 ?? 0,
                            )}
                          >
                            {netDv01}
                          </strong>
                        </span>
                        <i aria-hidden="true" />
                        <span className="markets-v4-instrument-group__dv01-metric">
                          <span>Net DV01 limit</span>
                          <strong>{netDv01Limit}</strong>
                        </span>
                        <i aria-hidden="true" />
                        <span className="markets-v4-instrument-group__dv01-metric">
                          <span>Gross DV01</span>
                          <strong>{grossDv01}</strong>
                        </span>
                        <i aria-hidden="true" />
                        <span className="markets-v4-instrument-group__dv01-metric">
                          <span>Gross DV01 limit</span>
                          <strong>{grossDv01Limit}</strong>
                        </span>
                      </span>
                    )}
                  </button>
                </h2>
              </header>

              <section
                ref={node => {
                  if (node) sectionRefs.current.set(group.key, node);
                  else sectionRefs.current.delete(group.key);
                }}
                id={sectionId}
                className={`markets-v4-instrument-group is-${group.key}`}
                aria-labelledby={titleId}
              >
                <div className="markets-v4-instrument-list">
                  {group.rows.map(row => {
                    const option = row.instrument.simplified_option;
                    const showsBondYield = group.key === 'bond';
                    const quoted = option
                      ? option.availability?.available === true
                      : ordinaryQuoteIsAvailable(row.asset);
                    const failed = option
                      ? isSimplifiedOptionSettlementFailed(option)
                      : false;
                    const selected = selectedInstrumentId === row.instrument.id;
                    const inDesk = executionTargetsInstrument(
                      executionIntent,
                      row.instrument.id,
                    );
                    const focus = () => selectInstrument(row.instrument);
                    const openTrade = () => openInstrumentOrder(row.instrument);
                    const position = option
                      ? `${formatters.commasNoDigits(option.contracts || 0)} contracts`
                      : formatters.commasNoDigits(row.instrument.amount);
                    const optionMetric = option
                      ? optionSecondaryMetric({
                          underlyingKind: row.asset.underlying_kind,
                          annualizedImpliedVolatility:
                            option.current_annualized_implied_volatility,
                          annualizedImpliedVolatilityDiff:
                            option.current_annualized_implied_volatility_diff,
                          basisPoints: option.current_basis_points,
                          basisPointsDiff: option.current_basis_points_diff,
                        })
                      : undefined;
                    const assessmentInstrument =
                      assessmentInstrumentByAsset.get(row.asset.id);
                    const knownInstrument =
                      assessmentInstrument ||
                      nonRecordingInstrumentByAsset.get(row.asset.id);
                    const assessmentEligible = Boolean(
                      assessment?.attached && assessmentInstrument,
                    );
                    const instrumentCode = assessmentInstrument?.code;
                    const currentAssignments = instrumentCode
                      ? currentAssignmentsFor(instrumentCode)
                      : [];
                    const laterAssignments = instrumentCode
                      ? laterAssignmentsFor(instrumentCode)
                      : [];
                    const pastExpanded = Boolean(
                      instrumentCode && expandedPastCodes.has(instrumentCode),
                    );
                    const storedSlider = instrumentCode
                      ? assessment?.persistWrites
                        ? assessment.sliders[instrumentCode]
                        : (previewSliders[instrumentCode] ??
                          assessment?.sliders[instrumentCode])
                      : undefined;
                    const sliderValue = instrumentCode
                      ? (draggingSliders[instrumentCode] ?? storedSlider ?? 0)
                      : 0;
                    const displayName = instrumentDisplayName(
                      row,
                      showBondCountryMaturityLabel,
                    );

                    return (
                      <div
                        key={row.instrument.id}
                        className={`markets-v4-instrument-shell${
                          dragTargetCode !== undefined &&
                          instrumentCode === dragTargetCode
                            ? ' is-drop-target'
                            : ''
                        }${assessmentEligible ? ' can-accept-news' : ''}`}
                        data-assessment-code={knownInstrument?.code}
                        data-assessment-scoring={
                          knownInstrument?.assessment_scoring
                        }
                        onDragOver={event => {
                          if (!assessmentEligible || !assessment?.canWrite) {
                            return;
                          }
                          event.preventDefault();
                          event.dataTransfer.dropEffect = 'copy';
                        }}
                        onDragEnter={event => {
                          if (!assessmentEligible || !assessment?.canWrite) {
                            return;
                          }
                          event.preventDefault();
                          const code = assessmentInstrument!.code;
                          const depth =
                            (dragDepthRef.current.get(code) || 0) + 1;
                          dragDepthRef.current.set(code, depth);
                          setDragTargetCode(code);
                        }}
                        onDragLeave={() => {
                          // Same guard as onDragEnter: an instrument that
                          // cannot accept news never increments the depth
                          // counter, so it must not run the bookkeeping either.
                          if (!assessmentEligible || !assessment?.canWrite) {
                            return;
                          }
                          if (!assessmentInstrument) return;
                          const depth = Math.max(
                            0,
                            (dragDepthRef.current.get(
                              assessmentInstrument.code,
                            ) || 0) - 1,
                          );
                          if (depth) {
                            dragDepthRef.current.set(
                              assessmentInstrument.code,
                              depth,
                            );
                          } else {
                            dragDepthRef.current.delete(
                              assessmentInstrument.code,
                            );
                            setDragTargetCode(current =>
                              current === assessmentInstrument.code
                                ? undefined
                                : current,
                            );
                          }
                        }}
                        onDrop={event => {
                          if (assessmentEligible && assessmentInstrument) {
                            dropNews(event, assessmentInstrument);
                          }
                        }}
                      >
                        <article
                          className={`markets-v4-instrument${
                            assessment?.attached ? ' has-assessment' : ''
                          }${
                            showsBondYield ? ' has-bond-yield' : ''
                          }${selected ? ' is-selected' : ''}${
                            inDesk ? ' is-in-desk' : ''
                          }${quoted ? '' : ' is-unquoted'}`}
                          onClick={openTrade}
                          aria-label={`${displayName} instrument`}
                        >
                          <div className="markets-v4-instrument__identity">
                            <div className="markets-v4-instrument__code">
                              {!hideInstrumentTicker && (
                                <span>{row.asset.ticker}</span>
                              )}
                              <small>{row.currencySymbol}</small>
                            </div>
                            <strong title={displayName}>{displayName}</strong>
                          </div>

                          <div className="markets-v4-instrument__market">
                            <div>
                              <span>{option ? 'Strike' : 'Bid'}</span>
                              <strong>
                                {option ? (
                                  option.current_strike == null ? (
                                    '—'
                                  ) : (
                                    <MarketMovement
                                      value={formatters.commas2Digits(
                                        option.current_strike,
                                      )}
                                      priceDiff={option.current_strike_diff}
                                      fitValue={fitMarketValues}
                                    />
                                  )
                                ) : quoted ? (
                                  quote(row, 'bid', fitMarketValues)
                                ) : (
                                  '—'
                                )}
                              </strong>
                            </div>
                            <div>
                              <span>{optionMetric?.label || 'Ask'}</span>
                              <strong>
                                {optionMetric ? (
                                  optionMetric.value === '—' ? (
                                    '—'
                                  ) : (
                                    <MarketMovement
                                      value={optionMetric.value}
                                      priceDiff={optionMetric.priceDiff}
                                      fitValue={fitMarketValues}
                                    />
                                  )
                                ) : quoted ? (
                                  quote(row, 'ask', fitMarketValues)
                                ) : (
                                  '—'
                                )}
                              </strong>
                            </div>
                            {showsBondYield && (
                              <div>
                                <span>Yield</span>
                                <strong>
                                  <MarketMovement
                                    value={bondYield(row, quoted)}
                                    fitValue={fitMarketValues}
                                  />
                                </strong>
                              </div>
                            )}
                          </div>

                          <div className="markets-v4-instrument__position">
                            <span>Position</span>
                            <strong>{position}</strong>
                            <small
                              className={pnlClass(
                                row.instrument.reporting_unrealized_pnl,
                              )}
                            >
                              P&amp;L{' '}
                              {formatters.commasNoDigits(
                                row.instrument.reporting_unrealized_pnl,
                              )}{' '}
                              {reportingCurrencySymbol}
                            </small>
                          </div>

                          {assessment?.attached && (
                            <div
                              className={`markets-v4-instrument__conviction${
                                assessmentEligible ? '' : ' is-empty'
                              }`}
                              onClick={
                                assessmentEligible
                                  ? event => event.stopPropagation()
                                  : undefined
                              }
                              onPointerDown={
                                assessmentEligible
                                  ? event => event.stopPropagation()
                                  : undefined
                              }
                            >
                              {assessmentEligible && instrumentCode && (
                                <>
                                  <div>
                                    <span>Conviction</span>
                                    <strong
                                      className={
                                        storedSlider === undefined
                                          ? 'is-unset'
                                          : sliderValue > 0
                                            ? 'is-positive'
                                            : sliderValue < 0
                                              ? 'is-negative'
                                              : ''
                                      }
                                    >
                                      {storedSlider === undefined &&
                                      draggingSliders[instrumentCode] ===
                                        undefined
                                        ? 'Unset'
                                        : sliderValue > 0
                                          ? `+${sliderValue}`
                                          : sliderValue}
                                    </strong>
                                  </div>
                                  <Slider
                                    min={-5}
                                    max={5}
                                    step={1}
                                    included={false}
                                    tooltip={{ open: false }}
                                    disabled={!assessment.canWrite}
                                    ariaLabelForHandle={`${instrumentCode} conviction`}
                                    value={sliderValue}
                                    onChange={value =>
                                      setDraggingSliders(current => ({
                                        ...current,
                                        [instrumentCode]: value as number,
                                      }))
                                    }
                                    onChangeComplete={value => {
                                      setDraggingSliders(current => {
                                        const next = { ...current };
                                        delete next[instrumentCode];
                                        return next;
                                      });
                                      if ((value as number) === storedSlider)
                                        return;
                                      if (assessment.persistWrites) {
                                        assessment.setSlider(
                                          instrumentCode,
                                          value as number,
                                          storedSlider,
                                        );
                                      } else {
                                        setPreviewSliders(current => ({
                                          ...current,
                                          [instrumentCode]: value as number,
                                        }));
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          )}

                          <div className="markets-v4-instrument__actions">
                            {option ? (
                              <div className="markets-v4-action-grid">
                                <span className="markets-v4-action--trade">
                                  <Button
                                    size="small"
                                    type="primary"
                                    icon={<SwapOutlined />}
                                    disabled={
                                      sessionBlocksTrading(session.status) ||
                                      !quoted ||
                                      failed
                                    }
                                    className={
                                      inDesk &&
                                      executionIntent.kind === 'optionOrder'
                                        ? 'is-in-desk'
                                        : ''
                                    }
                                    aria-pressed={
                                      inDesk &&
                                      executionIntent.kind === 'optionOrder'
                                    }
                                    onClick={event => {
                                      event.stopPropagation();
                                      openTrade();
                                    }}
                                  >
                                    Trade
                                  </Button>
                                </span>
                              </div>
                            ) : (
                              <InstrumentActions
                                row={row}
                                session={session}
                                onTrade={() => {
                                  openTrade();
                                }}
                                onODA={() => {
                                  focus();
                                  openODA(row.instrument.id);
                                }}
                                onDetails={
                                  group.key === 'etf'
                                    ? () => showETFDetails(row.asset.id)
                                    : undefined
                                }
                                tradeOpen={
                                  inDesk && executionIntent.kind === 'order'
                                }
                                odaOpen={
                                  inDesk && executionIntent.kind === 'oda'
                                }
                              />
                            )}
                          </div>
                        </article>

                        {(currentAssignments.length > 0 ||
                          laterAssignments.length > 0) && (
                          <div className="markets-v4-instrument-assignments">
                            {currentAssignments.map(renderAssignment)}
                            {laterAssignments.length > 0 && (
                              <div className="markets-v4-instrument-past-links">
                                <button
                                  type="button"
                                  className="markets-v4-instrument-past-links__toggle"
                                  aria-expanded={pastExpanded}
                                  onClick={event => {
                                    event.stopPropagation();
                                    setExpandedPastCodes(current => {
                                      const next = new Set(current);
                                      if (instrumentCode) {
                                        if (next.has(instrumentCode)) {
                                          next.delete(instrumentCode);
                                        } else {
                                          next.add(instrumentCode);
                                        }
                                      }
                                      return next;
                                    });
                                  }}
                                >
                                  <span aria-hidden="true">
                                    {pastExpanded ? '▾' : '▸'}
                                  </span>
                                  Past news ({laterAssignments.length})
                                </button>
                                {pastExpanded &&
                                  laterAssignments.map(renderAssignment)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </React.Fragment>
          );
        })}
      </div>
      <nav
        className="markets-v4-instrument-bottom-stack"
        aria-label="Upcoming asset classes"
      >
        {groups.map((group, groupIndex) =>
          bottomGroupKeys.includes(group.key) ? (
            <button
              key={group.key}
              type="button"
              className={`markets-v4-instrument-bottom-stack__label is-${group.key}`}
              style={
                {
                  '--mv4-bottom-index': groups.length - groupIndex - 1,
                } as React.CSSProperties
              }
              aria-controls={`markets-v4-${group.key}-instruments`}
              aria-label={`Show ${group.label}, ${group.rows.length} instruments`}
              onClick={() => scrollToGroup(group.key, groupIndex)}
            >
              <span className="markets-v4-instrument-group__label">
                {group.label}
              </span>
              <span
                className="markets-v4-instrument-group__count"
                aria-hidden="true"
              >
                {group.rows.length}
              </span>
            </button>
          ) : null,
        )}
      </nav>
    </div>
  );
};

export default InstrumentStack;
